import { RiskBaseGlobal } from 'components/App/Global';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import deepMerge from 'deepmerge';
import delay from 'delay';
import {
  checkForUpdateAsync,
  fetchUpdateAsync,
  reloadAsync,
  UpdateCheckResult,
} from 'expo-updates';
import { NetworkError, StorageError } from 'lib/errors';
import { getDeviceString } from 'lib/getDeviceInformation';
import { pluralise } from 'lib/pluralise';
import { arrayUnique, randomKey } from 'lib/riskbase';
import { intersection, isError, noop } from 'lodash';
import { Platform } from 'react-native';
import {
  apiGet,
  apiPost,
  checkInternetConnection,
  OFFLINE_ERROR,
} from 'service/api';
import * as http from 'service/api/constants';
import {
  getAssetData,
  readLocallyAddedFile,
  getOrganisationData,
  removeAssetDirectory,
  removeAssetFile,
  removeAssetThumbnail,
  deleteLocallyAddedFile,
  removeOrganisationData,
  setAssetData,
  setAssetFile,
  setAssetThumbnail,
  setOrganisationData,
} from 'service/storage';
import {
  CHECK_FOR_APP_UPDATE_MANUAL,
  doCloudPropertiesResetModal,
  doCloudPropertiesSetLoadKey,
  doReportingSetSavedPages,
  ORGANISATIONS_DELETED,
  PAUSE_BG_NETWORK,
  SET_LOADED_ASSET,
  SET_SYNC_STATE,
} from 'store/actions';
import {
  ALERT,
  ALERT_ERROR,
  CLEAR_ALERT,
  CONFIRM,
  eventAssetDataUpdated,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  SHOW_BLOCKING_LOADER,
  doHideSyncModal,
  NAVIGATE_MODAL_BACK,
  doSetOfflineOnly,
} from 'store/app/actions';
import { selectAppInstances } from 'store/app/selectors';
import {
  selectAssetListAvatarIds,
  selectAssetListItemUnmerged,
  selectAssetListUnmerged,
  selectAssetOrganisationIds,
  selectIsAssetLoaded,
} from 'store/assets/selectors';
import { Asset, AssetFromSync } from 'store/assets/types';
import {
  UPDATE_USER_ORGANISATIONS,
  UPDATE_USER,
  doUserSetUrlToken,
  doUserOrganisationSet,
} from 'store/auth/actions';
import {
  selectUrlToken,
  selectUserId,
  selectUserReference,
  selectUserSelectedOrganisationId,
  selectUserTimestamp,
} from 'store/auth/selectors';
import { CLEAR_LOCAL_CHANGES } from 'store/changes/actions';
import {
  selectHasLocalChanges,
  selectChangedAssets,
  selectChangedAsset,
} from 'store/changes/selectors';
import { CATCH_ERROR, UPDATE_ASSET_ERROR } from 'store/error/actions';
import { selectErrors } from 'store/error/selectors';
import { ErrorState } from 'store/error/types';
import {
  doOrgLocalMergeRemoveKeys,
  SET_LOADED_ORGANISATION,
} from 'store/organisations/actions';
import {
  selectAssessmentTemplateIdsByOrganisationIds,
  selectIsOrganisationLoaded,
  selectOrganisationIds,
  selectOrganisationListItem,
} from 'store/organisations/selectors';
import { Organisation } from 'store/organisations/types';
import { sagaConfirm } from 'store/sagaHelpers';
import {
  ASSETS_DELETED,
  CHECK_FOR_APP_UPDATE,
  DATA_SYNC,
  DELETE_ASSETS,
  DELETE_ASSETS_WITH_CHANGE_CHECK,
  DELETE_ORGANISATIONS,
  DO_MANUAL_SYNC,
  doSyncDownloadProperties,
  DOWNLOAD_FILES,
  DOWNLOAD_THUMBNAILS,
  MANAGE_ASSET_FILE_DOWNLOADS,
  Q_DELETE_ASSETS,
  REMOVE_DOWNLOADED_FILES,
  REMOVE_DOWNLOADED_THUMBNAILS,
  SET_SYNCING_NONE,
  INIT_BG_LOOP,
  UPDATE_ASSET_LIST,
  UPDATE_ORGANISATION_LIST,
  UPLOAD_ERRORS,
  UPLOAD_FILE,
  UNPAUSE_BG_NETWORK,
  doSetFileUploadFailed,
  doSetAppUpdateAvailable,
  doSyncSetStatus,
} from 'store/sync/actions';
import {
  CHECK_FOR_APP_UPDATE_MS,
  DELAY_APP_UPDATE_MS,
  DELAY_SYNC_TIME,
  SYNC_DELAY_401_ERROR_MS,
  SYNC_DELAY_CHANGES_MS,
  SYNC_DELAY_NETWORK_ERROR_MS,
  SYNC_DELAY_NORMAL_MS,
  SYNC_LOOP_DELAY_MS,
} from 'store/sync/constants';
import {
  hasStartTimePassedLastUpdateCheckTime,
  selectDataSyncBlocked,
  selectNetworkQueueBlocked,
  selectAssetIdsToDownload,
  selectAssetsToRemove,
  selectAssetTimestamps,
  selectSyncFileDownloadQueue,
  selectSyncThumbnailDownloadQueue,
  selectFilesDownloadedByAsset,
  selectOrganisationTimestamps,
  selectShouldUploadErrors,
  selectServerTimestamp,
  selectSyncDownloadCleanData,
  selectSyncFileUploadQueue,
  selectSyncStatus,
} from 'store/sync/selectors';
import { SyncSteps, SyncResponse } from 'store/sync/types';
import { call, fork, put, select, take, takeEvery } from 'typed-redux-saga';

function* sendReduxPersistError() {
  try {
    const payload: { errors: ErrorState['errors']; device: string } = {
      device: getDeviceString(),
      errors: [
        {
          info: 'Redux persist error',
          message: RiskBaseGlobal.reduxPersistError!.message,
          name: RiskBaseGlobal.reduxPersistError!.name,
          stack: RiskBaseGlobal.reduxPersistError!.stack || '',
          timestamp: new Date().toISOString(),
        },
      ],
    };
    yield* call(apiPost, http.ERRORS, {
      payload,
    });
  } catch (_) {
    noop();
  }
}

function* showReduxPersistError() {
  yield* put(
    ALERT({
      buttonText: 'Reload App',
      icon: ICONS.CircleExclamationLight,
      text:
        'RiskBase cannot save data to your device. You will lose data if you continue using the app in this state.\n\n' +
        `Please take a screenshot of this error and send it to support@riskbase.uk\n\n${
          RiskBaseGlobal.reduxPersistError!.name
        }. ${RiskBaseGlobal.reduxPersistError!.message}`,
      title: 'Application Error',
    }),
  );
  yield* take(CLEAR_ALERT);
  yield* put(SHOW_BLOCKING_LOADER());
  if (Platform.OS === 'web') {
    window.location.reload();
  } else {
    yield* call(reloadAsync);
  }
}

function* bg(): Generator {
  try {
    // Completely stop the app if a redux persist error occurs
    if (RiskBaseGlobal.reduxPersistError) {
      yield* fork(sendReduxPersistError);
      yield* call(showReduxPersistError);
      return;
    }
    const syncStartTime = Date.now();
    // Check for a new version of the application
    const checkAppUpdate = yield* select(
      hasStartTimePassedLastUpdateCheckTime,
      syncStartTime,
    );
    if (checkAppUpdate) {
      yield put(CHECK_FOR_APP_UPDATE());
      return;
    }
    // Check for errors that need uploading and if they should be uploaded now
    const shouldUploadErrors = yield* select(
      selectShouldUploadErrors,
      syncStartTime,
    );
    if (shouldUploadErrors) {
      yield put(UPLOAD_ERRORS.request());
      return;
    }
    // Delete assets queued for deletion
    const assetsToRemove = yield* select(selectAssetsToRemove);
    if (assetsToRemove.length) {
      yield put(Q_DELETE_ASSETS({ ids: assetsToRemove }));
      return;
    }
    // Run data changes (data sync, thumbnails download, files upload)
    const networkQueueBlocked = yield* select(
      selectNetworkQueueBlocked,
      syncStartTime,
    );

    if (!networkQueueBlocked) {
      // Sync Data
      const dataSyncBlocked = yield* select(
        selectDataSyncBlocked,
        syncStartTime,
      );
      if (!dataSyncBlocked) {
        yield* put(doSyncSetStatus({ step: SyncSteps.data, count: 0 }));
        yield put(DATA_SYNC.request());
        return;
      }
      // Download Thumbnails
      const downloadedThumbnailsQueue = Object.keys(
        yield* select(selectSyncThumbnailDownloadQueue),
      );
      if (downloadedThumbnailsQueue.length) {
        yield* put(
          doSyncSetStatus({
            step: SyncSteps.thumbnailDownload,
            count: downloadedThumbnailsQueue.length,
          }),
        );
        yield put(DOWNLOAD_THUMBNAILS.request());
        return;
      }
      // Download Files
      const downloadedFilesQueue = Object.keys(
        yield* select(selectSyncFileDownloadQueue),
      );
      if (downloadedFilesQueue.length) {
        yield* put(
          doSyncSetStatus({
            step: SyncSteps.fileDownload,
            count: downloadedFilesQueue.length,
          }),
        );
        yield put(DOWNLOAD_FILES.request());
        return;
      }
      // Upload File
      const fileUploadQueue = yield* select(selectSyncFileUploadQueue);
      if (fileUploadQueue.length) {
        yield* put(
          doSyncSetStatus({
            step: SyncSteps.fileUpload,
            count: fileUploadQueue.length,
          }),
        );
        yield put(UPLOAD_FILE.request(fileUploadQueue[0]!));
        return;
      }
    }
  } catch (error) {
    yield* put(CATCH_ERROR({ error }));
  }
  if ((yield* select(selectSyncStatus)).step !== SyncSteps.waiting) {
    yield* put(SET_SYNCING_NONE());
    yield* put(doSyncSetStatus({ step: SyncSteps.finished, count: 0 }));
    yield delay(0); // delay to allow "finished" to be set
    yield* put(doSyncSetStatus({ step: SyncSteps.waiting, count: 0 }));
  }
  yield delay(SYNC_LOOP_DELAY_MS);
  yield* call(bg);
}

function* mergeAssetsIntoStorage(
  fromSync: Record<string, AssetFromSync>,
): Generator {
  for (const assetId of Object.keys(fromSync)) {
    try {
      const incomingAsset = fromSync[assetId]!;
      const assetExists = !!(yield* select((state) =>
        selectAssetListItemUnmerged(state, assetId),
      ));

      let mergedAsset: Asset;
      let updatedKeys: Partial<(keyof AssetFromSync)[]> = [];
      if (assetExists) {
        const assetData = yield* call(getAssetData, assetId);
        mergedAsset = {
          ...assetData,
          ...incomingAsset,
          dv: {
            ...assetData.dv,
            ...incomingAsset.dv,
          },
        };
        updatedKeys = Object.keys(fromSync[assetId]!).filter(
          (k) => !['id', 'reference', 'organisation_id'].includes(k),
        ) as Partial<(keyof AssetFromSync)[]>;
      } else {
        mergedAsset = incomingAsset as Asset;
      }

      yield* call(setAssetData, assetId, mergedAsset);

      const localChanges = yield* select(selectChangedAsset, assetId);

      yield* put(
        eventAssetDataUpdated({
          route: 'sync',
          assetId,
          data: localChanges
            ? deepMerge(mergedAsset, localChanges, {
                arrayMerge: (_, a) => a,
              })
            : mergedAsset,
          updatedKeys,
        }),
      );

      // Update asset if it's currently loaded
      if (yield* select(selectIsAssetLoaded, assetId)) {
        yield* put(SET_LOADED_ASSET(mergedAsset));
      }
    } catch (error) {
      yield* put(CATCH_ERROR({ error }));
    }
  }
}

function* mergeOrganisationsIntoStorage(
  fromSync: Record<string, Partial<Organisation>>,
): Generator {
  for (const organisationId of Object.keys(fromSync)) {
    try {
      const incomingOrganisation = fromSync[organisationId]!;
      const organisationExists = !!(yield* select((state) =>
        selectOrganisationListItem(state, organisationId),
      ));

      let mergedOrganisation: Organisation;
      if (organisationExists) {
        const organisationData = yield* call(
          getOrganisationData,
          organisationId,
        );
        mergedOrganisation = {
          ...organisationData,
          ...incomingOrganisation,
          dv: {
            ...organisationData.dv,
            ...incomingOrganisation.dv,
          },
        };
      } else {
        mergedOrganisation = incomingOrganisation as Organisation;
      }

      yield* call(setOrganisationData, organisationId, mergedOrganisation);

      // Update organisation if it's currently loaded
      if (yield* select(selectIsOrganisationLoaded, organisationId)) {
        yield* put(SET_LOADED_ORGANISATION(mergedOrganisation));
      }

      yield* put(
        doOrgLocalMergeRemoveKeys({
          id: organisationId,
          keys: Object.keys(incomingOrganisation),
        }),
      );
    } catch (error) {
      yield* put(CATCH_ERROR({ error }));
    }
  }
}

export const syncSideEffects = [
  takeEvery(INIT_BG_LOOP, bg),

  takeEvery(DATA_SYNC.request, function* f() {
    const reducerTsTo1 = <T = Record<string, number>>(
      ids: string[],
      init: T = {} as T,
    ): T =>
      ids.reduce<T>(
        (acc, assetId) => ({
          ...acc,
          [assetId]: 1,
        }),
        init,
      );

    try {
      yield* call(checkInternetConnection, {
        throwErrorIfOffline: true,
        forcePing: false,
        doPingIfWeb: true,
      });
      const currentTime = Date.now();
      const downloadCleanData = yield* select(selectSyncDownloadCleanData);

      const response = yield* apiPost<SyncResponse>(
        http.SYNC,
        {
          cleanData: downloadCleanData ? 1 : 0,
          serverTimestamp: yield* select(selectServerTimestamp),
          assetTimestamps: reducerTsTo1(
            yield* select(selectAssetIdsToDownload),
            yield* select(selectAssetTimestamps),
          ),
          organisation:
            (yield* select(selectUserSelectedOrganisationId)) || null,
          organisationTimestamps: yield* select(selectOrganisationTimestamps),
          organisationAssessmentTemplates: yield* select(
            selectAssessmentTemplateIdsByOrganisationIds,
          ),
          userTimestamp: (yield* select(selectUserTimestamp)) || 0,
          assets: yield* select(selectChangedAssets),
          device: getDeviceString(),
          instances: yield* select(selectAppInstances),
          includeUrlToken: (yield* select(selectUrlToken)) ? undefined : true,
        },
        {
          skipConnectionCheck: true,
        },
      );

      if (
        response.asset_errors &&
        Object.keys(response.asset_errors).length > 0
      ) {
        yield* put(UPDATE_ASSET_ERROR(response.asset_errors));
        yield* put(
          SET_SYNC_STATE({
            skipBgDataSyncUntilTime: Date.now() + DELAY_SYNC_TIME,
          }),
        );
      } else {
        yield* put(UPDATE_ASSET_ERROR(undefined));
      }

      // Add to storage first
      if (Object.keys(response.organisations).length) {
        yield* call(mergeOrganisationsIntoStorage, response.organisations);
        yield* put(UPDATE_ORGANISATION_LIST(response));
      }

      // Add to storage first
      yield* call(mergeAssetsIntoStorage, response.assets);
      yield* put(UPDATE_ASSET_LIST(response));

      if (response.user) {
        yield* put(UPDATE_USER(response.user));
      }
      if (response.urlToken) {
        yield* put(doUserSetUrlToken(response.urlToken));
      }

      if (response.userOrganisations) {
        yield* put(UPDATE_USER_ORGANISATIONS(response.userOrganisations));
      }

      const currentOrganisationId = yield* select(
        selectUserSelectedOrganisationId,
      );
      if (
        response.organisation &&
        response.organisation.id === currentOrganisationId
      ) {
        yield* put(
          doUserOrganisationSet({ organisation: response.organisation }),
        );
      }

      yield* put(
        CLEAR_LOCAL_CHANGES({
          asset_errors: response.asset_errors,
          timestamp: currentTime,
        }),
      );

      // Delete assets from device but only if they have no unsynced changes
      if (response.deleteAssets?.length) {
        yield* put(
          DELETE_ASSETS_WITH_CHANGE_CHECK({ ids: response.deleteAssets }),
        );
        yield* take(ASSETS_DELETED);
      }
      // Force delete assets from device even if they have unsynced changes
      if (response.forceDeleteAssets?.length) {
        yield* put(DELETE_ASSETS({ ids: response.forceDeleteAssets }));
        yield* take(ASSETS_DELETED);
      }

      yield* put(doReportingSetSavedPages({ pages: response.pages }));
      yield* put(doSetOfflineOnly(response.offlineOnly));

      const assets = yield* select(selectAssetListUnmerged);
      yield* put(MANAGE_ASSET_FILE_DOWNLOADS(assets));
      yield* put(DATA_SYNC.success(response));

      yield* put(
        SET_SYNC_STATE({
          lastBgDataSyncTime: currentTime,
          nextBgDataSyncTime: (yield* select(selectHasLocalChanges))
            ? Date.now() + SYNC_DELAY_CHANGES_MS
            : Date.now() + SYNC_DELAY_NORMAL_MS,
        }),
      );
      yield* call(bg);
    } catch (error) {
      if (!(error instanceof NetworkError)) {
        yield* put(CATCH_ERROR({ error }));
      }
      if (isError(error)) {
        if (
          error.message === 'ClientOutOfDateMinor' ||
          error.message === 'ClientOutOfDateMajor'
        ) {
          yield* put(doHideSyncModal());
          yield* put(
            NAVIGATE_MODAL({
              screen: ModalScreens.UpgradeRequired,
              params: {
                major: error.message === 'ClientOutOfDateMajor',
                withClose: true,
              },
            }),
          );
        }
        if (
          error instanceof NetworkError &&
          error.message === 'invalid-sync-confirmation'
        ) {
          yield delay(2000);
          yield* call(bg);
        } else {
          yield* put(DATA_SYNC.failure(error));
        }
      }
    }
  }),

  takeEvery(DOWNLOAD_THUMBNAILS.request, function* f() {
    const fileIdToAssetId = yield* select(selectSyncThumbnailDownloadQueue);
    const fileIds = Object.keys(fileIdToAssetId);
    const assetListAvatarIds = intersection(
      yield* select(selectAssetListAvatarIds),
      fileIds,
    );
    const finalFileIds = assetListAvatarIds.length
      ? arrayUnique([...assetListAvatarIds, ...fileIds]).slice(0, 1000)
      : fileIds.slice(0, 1000);
    try {
      const response = yield* apiPost<{
        files: Record<string, { body: string; width: number; height: number }>;
      }>(http.THUMBNAILS, {
        files: finalFileIds,
      });
      const savedThumbnails: {
        id: string;
        width: number;
        height: number;
      }[] = [];

      for (const id of Object.keys(response.files)) {
        try {
          yield* call(
            setAssetThumbnail,
            fileIdToAssetId[id]!,
            id,
            response.files[id]!.body,
          );
          savedThumbnails.push({
            id,
            width: response.files[id]!.width,
            height: response.files[id]!.height,
          });
        } catch (error) {
          yield* put(
            CATCH_ERROR({
              error,
              info: { _: `Saving downloaded thumbnail ${id}` },
            }),
          );
        }
      }
      yield* put(DOWNLOAD_THUMBNAILS.success(savedThumbnails));
      yield* call(bg);
    } catch (error) {
      if (isError(error)) {
        yield* put(DOWNLOAD_THUMBNAILS.failure(error));
      }
    }
  }),

  takeEvery(DOWNLOAD_FILES.request, function* f() {
    const fileIds = yield* select(selectSyncFileDownloadQueue);
    try {
      const response = yield* apiPost<{ files: Record<string, string> }>(
        http.FILES,
        {
          files: Object.keys(fileIds),
        },
      );
      const savedFileIds: string[] = [];

      for (const id of Object.keys(response.files)) {
        try {
          yield* call(setAssetFile, fileIds[id]!, id, response.files[id]!);
          savedFileIds.push(id);
        } catch (error) {
          yield* put(
            CATCH_ERROR({
              error,
              info: { _: `Saving downloaded file ${id}` },
            }),
          );
        }
      }
      yield* put(DOWNLOAD_FILES.success(savedFileIds));
      yield* call(bg);
    } catch (error) {
      if (isError(error)) {
        yield* put(DOWNLOAD_FILES.failure(error));
      }
    }
  }),

  takeEvery(UPLOAD_FILE.request, function* f(action) {
    const fileId = action.payload;
    try {
      yield* call(checkInternetConnection, {
        throwErrorIfOffline: true,
        forcePing: false,
        doPingIfWeb: true,
      });
      const file = yield* call(readLocallyAddedFile, fileId);
      yield* call(
        apiPost,
        http.UPLOAD_FILE(fileId),
        { b: file, c: Date.now() / 1000, length: file.length },
        {
          skipConnectionCheck: true,
        },
      );
      yield* call(deleteLocallyAddedFile, fileId);
      yield* put(UPLOAD_FILE.success(fileId));
      yield* call(bg);
    } catch (error) {
      if (error instanceof StorageError) {
        // Mark the file as successful. This can happen when a
        // file was uploaded successful but the store wasn't updated
        yield* put(UPLOAD_FILE.success(fileId));
        yield* call(bg);
      } else if (isError(error)) {
        if (
          error instanceof NetworkError &&
          error.message === 'invalid-file-confirmation'
        ) {
          yield delay(2000);
          yield* call(bg);
        } else {
          yield put(
            CATCH_ERROR({
              error,
              info: {
                _: 'StorageError when uploading file',
                fileId,
              },
            }),
          );
          yield* put(doSetFileUploadFailed({ fileId }));
          yield* put(UPLOAD_FILE.failure(error));
        }
      }
    }
  }),

  takeEvery(
    [
      DATA_SYNC.failure,
      DOWNLOAD_THUMBNAILS.failure,
      DOWNLOAD_FILES.failure,
      UPLOAD_FILE.failure,
    ],
    function* f(action) {
      const error = action.payload;
      if (error instanceof NetworkError) {
        yield* put(
          SET_SYNC_STATE({
            skipBgNetworkUntilTime:
              Date.now() +
              (error.isUnauthorized
                ? SYNC_DELAY_401_ERROR_MS
                : SYNC_DELAY_NETWORK_ERROR_MS),
          }),
        );
      }
      yield* call(bg);
    },
  ),

  takeEvery(REMOVE_DOWNLOADED_THUMBNAILS, function* f(action) {
    const { fileIds } = action.payload;
    const { assetId } = action.payload;
    const errors: string[] = [];

    for (const fileId of fileIds) {
      try {
        yield* call(removeAssetThumbnail, assetId, fileId);
      } catch (error) {
        if (isError(error)) {
          errors.push(`${assetId}-${fileId} - ${error.message}`);
        }
      }
    }
    if (errors.length) {
      yield* put(
        CATCH_ERROR({
          error: new Error('Error removing downloaded thumbnails'),
          info: { issues: errors },
        }),
      );
    }
  }),

  takeEvery(REMOVE_DOWNLOADED_FILES, function* f(action) {
    const { fileIds } = action.payload;
    const { assetId } = action.payload;
    const errors: string[] = [];

    for (const fileId of fileIds) {
      try {
        yield* call(removeAssetFile, assetId, fileId);
      } catch (error) {
        if (isError(error)) {
          errors.push(`${assetId}-${fileId} - ${error.message}`);
        }
      }
    }
    if (errors.length) {
      yield* put(
        CATCH_ERROR({
          error: new Error('Error removing downloaded files'),
          info: { issues: errors },
        }),
      );
    }
  }),

  takeEvery(UPLOAD_ERRORS.request, function* f() {
    try {
      const errors = __DEV__ ? [] : yield* select(selectErrors);
      if (errors.length) {
        yield* call(apiPost, http.ERRORS, {
          device: getDeviceString(),
          errors,
          userId: yield* select(selectUserReference),
        });
      }
      yield put(UPLOAD_ERRORS.success());
    } catch (_) {
      // Do nothing with the error
      yield put(UPLOAD_ERRORS.failure());
    }
    yield* call(bg);
  }),

  takeEvery(CHECK_FOR_APP_UPDATE, function* f() {
    let delayAppUpdateMs = CHECK_FOR_APP_UPDATE_MS;
    try {
      const update: Pick<UpdateCheckResult, 'isAvailable'> =
        __DEV__ || Platform.OS === 'web'
          ? { isAvailable: false }
          : yield* call(checkForUpdateAsync);
      if (update.isAvailable) {
        const fetchedUpdate = yield* call(fetchUpdateAsync);
        if (fetchedUpdate.isNew) {
          const confirmAction = 'ACTION_CANCEL_APP_UPDATE';
          const cancelAction = 'ACTION_CONFIRM_APP_UPDATE';
          yield* put(
            CONFIRM({
              cancelText: 'Remind Later',
              confirmText: 'Update',
              dismissable: false,
              onCancel: { type: cancelAction },
              onConfirm: { type: confirmAction },
              text: "Updating only takes a second but if you're in the middle of something we'll remind you later",
              title: 'App Update Available',
            }),
          );
          if (
            (yield* take([confirmAction, cancelAction])).type === confirmAction
          ) {
            yield* call(reloadAsync);
            return;
          }
          delayAppUpdateMs = DELAY_APP_UPDATE_MS;
        }
      }
    } catch (_) {
      noop();
    }
    yield* put(
      SET_SYNC_STATE({ nextAppUpdateCheckTime: Date.now() + delayAppUpdateMs }),
    );
    yield* call(bg);
  }),

  takeEvery(CHECK_FOR_APP_UPDATE_MANUAL, function* f() {
    let delayAppUpdateMs = CHECK_FOR_APP_UPDATE_MS;
    yield* put(PAUSE_BG_NETWORK());
    const blKey = randomKey();
    try {
      yield* put(SHOW_BLOCKING_LOADER(blKey));
      let expoUpdateAvailable = false;
      const update: Pick<UpdateCheckResult, 'isAvailable'> =
        __DEV__ || Platform.OS === 'web'
          ? { isAvailable: false }
          : yield* call(checkForUpdateAsync);
      if (update.isAvailable) {
        const fetchedUpdate = yield* call(fetchUpdateAsync);
        if (fetchedUpdate.isNew) {
          yield* put(
            SET_SYNC_STATE({
              nextAppUpdateCheckTime: Date.now() + delayAppUpdateMs,
            }),
          );
          expoUpdateAvailable = true;
          yield* put(HIDE_BLOCKING_LOADER(blKey));
          const confirmAction = 'ACTION_CANCEL_APP_UPDATE';
          const cancelAction = 'ACTION_CONFIRM_APP_UPDATE';
          yield* put(
            CONFIRM({
              cancelText: 'Remind Later',
              confirmText: 'Update',
              dismissable: false,
              onCancel: { type: cancelAction },
              onConfirm: { type: confirmAction },
              text: "Updating only takes a second but if you're in the middle of something we'll remind you later",
              title: 'App Update Available',
            }),
          );
          if (
            (yield* take([confirmAction, cancelAction])).type === confirmAction
          ) {
            yield* call(reloadAsync);
            return;
          }
        }
      }
      if (!expoUpdateAvailable) {
        const upgradeAvailable = yield* apiGet<{
          status: false | 'major' | 'minor';
        }>(http.apiAppUpdateAvailable);
        yield* put(HIDE_BLOCKING_LOADER(blKey));
        yield* put(
          doSetAppUpdateAvailable({ status: upgradeAvailable.status }),
        );
        if (upgradeAvailable.status) {
          yield* put(
            NAVIGATE_MODAL({
              screen: ModalScreens.UpgradeRequired,
              params: {
                major: upgradeAvailable.status === 'major',
                withClose: !!(yield* select(selectUserId)),
              },
            }),
          );
        } else {
          yield* put(
            ALERT({
              text: "You're on the latest version of RiskBase",
              title: 'No Update Available',
            }),
          );
        }
      }
      delayAppUpdateMs = DELAY_APP_UPDATE_MS;
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER(blKey));
      yield* put(
        ALERT_ERROR({
          error: OFFLINE_ERROR,
        }),
      );
      yield* put(CATCH_ERROR({ error }));
    }
    yield* put(
      SET_SYNC_STATE({ nextAppUpdateCheckTime: Date.now() + delayAppUpdateMs }),
    );
    yield* put(UNPAUSE_BG_NETWORK());
  }),

  takeEvery(Q_DELETE_ASSETS, function* f(action) {
    yield* put(DELETE_ASSETS(action.payload));
    yield* take(ASSETS_DELETED);
    yield* call(bg);
  }),

  takeEvery(DELETE_ASSETS_WITH_CHANGE_CHECK, function* f(action) {
    const assetIds = action.payload.ids;
    const changedAssets = yield* select(selectChangedAssets);
    yield* put(
      DELETE_ASSETS({
        ids: assetIds.filter((assetId) => !changedAssets[assetId]),
      }),
    );
    yield* take(ASSETS_DELETED);
  }),

  takeEvery(DELETE_ASSETS, function* f(action) {
    const assetIds = action.payload.ids;
    if (assetIds.length) {
      for (const assetId of assetIds) {
        try {
          yield* call(removeAssetDirectory, assetId);
        } catch (error) {
          yield* put(
            CATCH_ERROR({
              error,
              info: {
                description: 'When deleting asset (removeAssetDirectory)',
              },
            }),
          );
        }
      }
      // Check if any organisations can be deleted
      const assetOrganisationIds = yield* select(selectAssetOrganisationIds);
      const deleteOrganisationIds = (yield* select(
        selectOrganisationIds,
      )).filter(
        (organisationId) => !assetOrganisationIds.includes(organisationId),
      );
      if (deleteOrganisationIds.length) {
        yield* put(DELETE_ORGANISATIONS({ ids: deleteOrganisationIds }));
        yield* take(ORGANISATIONS_DELETED);
      }
    }
    yield* put(ASSETS_DELETED({ ids: assetIds }));
  }),

  takeEvery(DELETE_ORGANISATIONS, function* f(action) {
    const organisationIds = action.payload.ids;

    for (const organisationId of organisationIds) {
      try {
        yield* call(removeOrganisationData, organisationId);
      } catch (error) {
        yield* put(
          CATCH_ERROR({
            error,
            info: {
              description:
                'When deleting organisation (removeOrganisationData)',
            },
          }),
        );
      }
    }
    yield* put(ORGANISATIONS_DELETED({ ids: organisationIds }));
  }),

  takeEvery(MANAGE_ASSET_FILE_DOWNLOADS, function* f(action) {
    const assetsFromSync = action.payload;
    const assetIds = Object.keys(assetsFromSync);
    if (!assetIds.length) {
      return;
    }

    const assetList = yield* select(selectAssetListUnmerged);
    // const thumbnailsDownloadedByAsset = yield* select(
    //   selectThumbnailsDownloadedByAsset,
    // );
    const filesDownloadedByAsset = yield* select(selectFilesDownloadedByAsset);

    for (const assetId of assetIds) {
      try {
        const localAsset = assetList[assetId];
        if (localAsset) {
          /**
           * Old thumbnails are no longer deleted. If a new file wasn't uploaded to
           * RiskBase before the data sync, the thumbnail would be deleted locally.
           */
          // Delete downloaded thumbnails
          // const { downloadThumbnails } = assetsFromSync[assetId];
          // if (downloadThumbnails !== undefined) {
          //   const thumbnailsDownloaded =
          //     thumbnailsDownloadedByAsset[assetId] || [];

          //   const removeThumbnailIds =
          //     downloadThumbnails.length > 0
          //       ? thumbnailsDownloaded.filter(
          //           (fileId) => !downloadThumbnails.includes(fileId),
          //         )
          //       : thumbnailsDownloaded;

          //   if (removeThumbnailIds.length) {
          //     yield* put(
          //       REMOVE_DOWNLOADED_THUMBNAILS({
          //         assetId,
          //         fileIds: removeThumbnailIds,
          //       }),
          //     );
          //   }
          // }

          // Delete downloaded files
          const { downloadFiles } = assetsFromSync[assetId]!;
          const filesDownloaded = filesDownloadedByAsset[assetId] || [];
          const removeFileIds =
            downloadFiles && downloadFiles.length > 0
              ? filesDownloaded.filter(
                  (fileId) => !downloadFiles.includes(fileId),
                )
              : filesDownloaded;

          if (removeFileIds.length) {
            yield* put(
              REMOVE_DOWNLOADED_FILES({
                assetId,
                fileIds: removeFileIds,
              }),
            );
          }
        }
      } catch (error) {
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(doSyncDownloadProperties, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    const addAssetIds = action.payload;
    yield* put(DO_MANUAL_SYNC({ addAssetIds }));
    const taken = yield* take([DATA_SYNC.success, DATA_SYNC.failure]);
    yield* put(HIDE_BLOCKING_LOADER());
    if (taken.type === DATA_SYNC.failure.toString()) {
      yield* put(ALERT_ERROR({ error: taken.payload, action }));
      return;
    }
    const closeDownload = yield* sagaConfirm({
      title: `${pluralise(addAssetIds, 'Property', 'Properties')} Downloaded`,
      text:
        `You can now perform assessments for ` +
        `${pluralise(addAssetIds, 'this property', 'these properties')} ` +
        `without an internet connection`,
      cancelText: 'Download More',
      confirmText: 'Done',
    });
    if (closeDownload) {
      yield* put(NAVIGATE_MODAL_BACK());
    } else {
      yield* put(doCloudPropertiesResetModal());
      yield* put(doCloudPropertiesSetLoadKey(randomKey()));
    }
  }),
];
